import { forwardRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { logout, getClaims, type Role } from "../lib/api.js";
import { Icon, type IconName } from "./Icon.js";

interface NavItem {
  key: string;
  icon: IconName;
  label: string;
  to: string;
}
interface NavZone {
  sec: string;
  items: NavItem[];
  /** low-frequency items, tucked behind a "もっと見る" disclosure to keep the rail non-scrolling */
  more?: NavItem[];
}

/**
 * Console navigation, organised into 3 dense zones (作成 / ワークフロー / 管理). Low-frequency
 *管理 items live behind a disclosure so the whole rail fits a laptop viewport without scrolling.
 */
const NAV: NavZone[] = [
  {
    sec: "作成",
    items: [
      { key: "dashboard", icon: "dashboard", label: "ダッシュボード", to: "/dashboard" },
      { key: "capture", icon: "capture", label: "撮影する", to: "/capture/video" },
    ],
  },
  {
    sec: "ワークフロー",
    items: [
      { key: "library", icon: "library", label: "ライブラリ", to: "/library" },
      { key: "inbox", icon: "inbox", label: "受信箱", to: "/inbox" },
      { key: "approval", icon: "approval", label: "承認待ち", to: "/approval" },
      { key: "ai-review", icon: "ai-review", label: "AIレビュー", to: "/ai-review" },
    ],
  },
  {
    sec: "管理",
    items: [
      { key: "members", icon: "members", label: "メンバー・権限", to: "/members" },
      { key: "stores", icon: "industry-food", label: "店舗・事業室", to: "/stores" },
      { key: "settings", icon: "settings", label: "設定", to: "/settings" },
      { key: "analytics", icon: "analytics", label: "分析", to: "/analytics" },
      { key: "learning", icon: "learning", label: "学習管理", to: "/learning" },
    ],
    more: [
      { key: "multilingual", icon: "multilingual", label: "多言語", to: "/multilingual" },
      { key: "studio", icon: "studio", label: "テンプレ・部品", to: "/studio" },
      { key: "versions", icon: "versions", label: "バージョン", to: "/versions" },
      { key: "feedback", icon: "feedback", label: "フィードバック", to: "/feedback" },
      { key: "ops", icon: "ops", label: "運用", to: "/ops" },
      { key: "billing", icon: "billing", label: "プラン・課金", to: "/billing" },
    ],
  },
];

const ROLE_LABEL: Record<Role, string> = {
  owner: "オーナー",
  admin: "管理者",
  editor: "編集者",
  reviewer: "レビュアー",
  viewer: "閲覧者",
};

function navLinkClass({ isActive }: { isActive: boolean }) {
  return `navlink${isActive ? " active" : ""}`;
}

function NavRow({ it, onNavigate }: { it: NavItem; onNavigate?: () => void }) {
  return (
    <NavLink to={it.to} className={navLinkClass} onClick={onNavigate}>
      <span className="ni" aria-hidden="true">
        <Icon name={it.icon} />
      </span>
      <span>{it.label}</span>
    </NavLink>
  );
}

interface SidebarProps {
  /** called when a destination is chosen, so the mobile drawer can close itself */
  onNavigate?: () => void;
}

export const Sidebar = forwardRef<HTMLElement, SidebarProps>(function Sidebar({ onNavigate }, ref) {
  const loc = useLocation();
  const morePaths = NAV.flatMap((z) => z.more ?? []).map((i) => i.to);
  // Which binder section the current route lives in — drives the shape/position active cue.
  const isZoneActive = (z: NavZone) =>
    [...z.items, ...(z.more ?? [])].some(
      (i) => loc.pathname === i.to || loc.pathname.startsWith(i.to + "/"),
    );

  // Expand the disclosure only when the active route lives inside it (so it is visible).
  const [open, setOpen] = useState(() => morePaths.some((p) => loc.pathname === p));

  const claims = getClaims();
  const role = (claims?.roles?.[0] as Role | undefined) ?? "viewer";
  const roleLabel = ROLE_LABEL[role] ?? role;
  const scope = claims?.stores?.[0] ?? claims?.org ?? "—";

  return (
    <aside ref={ref} id="console-nav" className="side" aria-label="管理ナビゲーション" tabIndex={-1}>
      {/* decorative binding: cloth-tape notebook spine + cream title label (背クロス+題箋, aria-hidden).
          It sits on the fixed .side frame, OUTSIDE .side-inner, so it never scrolls with the nav list. */}
      <span className="side-spine" aria-hidden="true">
        <i>TEBIKI</i>
      </span>
      <div className="side-inner">
        <Link className="brand" to="/dashboard" style={{ textDecoration: "none" }}>
          <span className="glyph" aria-hidden="true" />
          TEBIKI
        </Link>

        {NAV.map((z) => (
        <div key={z.sec} role="group" aria-label={z.sec}>
          <div className={`navsec${isZoneActive(z) ? " is-active" : ""}`}>
            <span>{z.sec}</span>
          </div>
          {z.items.map((it) => (
            <NavRow key={it.key} it={it} onNavigate={onNavigate} />
          ))}
          {z.more ? (
            <>
              <button
                type="button"
                className="navmore"
                aria-expanded={open}
                aria-controls="nav-more"
                onClick={() => setOpen((v) => !v)}
              >
                <span className="ni" aria-hidden="true">
                  <Icon name="more" />
                </span>
                <span>もっと見る</span>
                <span className="chev" aria-hidden="true">
                  <Icon name="chevron" size={14} />
                </span>
              </button>
              {open ? (
                <div id="nav-more" className="more-group" role="group" aria-label="その他の管理メニュー">
                  {z.more.map((it) => (
                    <NavRow key={it.key} it={it} onNavigate={onNavigate} />
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ))}

      <div className="sidefoot">
        <span className="avatar" aria-hidden="true">
          {roleLabel.slice(0, 1)}
        </span>
        <div className="who">
          <b>{roleLabel}</b>
          <span>{scope}</span>
        </div>
        <button type="button" className="out" aria-label="ログアウト" onClick={() => logout()}>
          <Icon name="power" size={16} />
        </button>
      </div>
      </div>
    </aside>
  );
});
